import { ModalRoot } from '@cian/ui-kit';
import { ArticleParagraph1 } from '@cian/ui-kit/typography';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { useFiltersContext } from '../../../../packages/Filters/shared/utils/useFiltersContext';
import { getRangeValue } from '../../../../packages/JsonQuery';
import { ETestId } from '../../../common/constants';
import { selectResultsJsonQuery } from '../../../common/selectors/results';
import { getPriceLabel } from '../../../common/utils/getPriceLabel';
import { Chip } from '../../components/RedesignedFilters';
import { useAdaptModalToOpenedKeyboard } from '../../hooks/useAdaptModalToOpenedKeyboard';
import { useChip } from '../../hooks/useChip';
import { useMobilePriceCorrection } from '../../hooks/useMobilePriceCorrection';
import { PriceModalContainer } from '../PriceModalContainer';
import { trackToastShow } from './tracking';

export const PriceChipContainer = () => {
  const { jsonQuery: filtersJsonQuery } = useFiltersContext();
  const resultsJsonQuery = useSelector(selectResultsJsonQuery);
  const { open, onChipClick, onReset, onCloseWithReset, onApplyClick } = useChip(['price']);
  const priceFromFilters = getRangeValue('price')(filtersJsonQuery);
  const priceFromResults = getRangeValue('price')(resultsJsonQuery);
  const { gte: priceMinFromFilters = null, lte: priceMaxFromFilters = null } = priceFromFilters || {};
  const { gte: priceMinFromResults = null, lte: priceMaxFromResults = null } = priceFromResults || {};
  const isMinChanged = priceMinFromFilters !== priceMinFromResults;
  const isMaxChanged = priceMaxFromFilters !== priceMaxFromResults;

  // Формируем лейбл для чипса
  const defaultPriceLabel = 'Цена';
  const priceLabel = getPriceLabel(priceMinFromFilters, priceMaxFromFilters);

  // Адаптируем модальное окно к открытой клавиатуре
  const { isKeyboardOpened, handleModalRef } = useAdaptModalToOpenedKeyboard({ isModalOpen: open });

  // Используем хук для автокоррекции цен
  const correctPrices = useMobilePriceCorrection();

  const handleApply = React.useCallback(() => {
    const { shouldBlockApply } = correctPrices({ isMinChanged, shouldBlockApplyOnFirstToast: true });
    if (!shouldBlockApply) {
      onApplyClick();
    }
  }, [correctPrices, isMinChanged, onApplyClick]);

  const onMinBlur = React.useCallback(() => {
    if (isMinChanged) {
      correctPrices({ isMinChanged: true, onToastShow: trackToastShow }); // При blur с min поля считаем что изменили min
    }
  }, [correctPrices, isMinChanged]);

  const onMaxBlur = React.useCallback(() => {
    if (isMaxChanged) {
      correctPrices({ isMinChanged: false, onToastShow: trackToastShow }); // При blur с max поля считаем что изменили max
    }
  }, [correctPrices, isMaxChanged]);

  return (
    <ModalRoot open={open} onOpenChange={onCloseWithReset}>
      <Chip onClick={onChipClick} testId={ETestId.RedesignedFiltersPriceChip}>
        <ArticleParagraph1>{priceLabel || defaultPriceLabel}</ArticleParagraph1>
      </Chip>
      <PriceModalContainer
        hideFooter={isKeyboardOpened}
        onClose={onCloseWithReset}
        onResetClick={onReset}
        onApplyClick={handleApply}
        onMinBlur={onMinBlur}
        onMaxBlur={onMaxBlur}
        ref={handleModalRef}
      />
    </ModalRoot>
  );
};
